import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import './ChatPanel.css';
import { streamMessage } from '../services/api';

function ChatPanel({ onModelUpdate, onReset, selectedBody, selectedFeature }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [toolCalls, setToolCalls] = useState([]);
  const [planText, setPlanText] = useState('');
  const [images, setImages] = useState([]);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, toolCalls, planText]);

  const processImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const maxSize = 1024;
          let { width, height } = img;
          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = Math.round(height * (maxSize / width));
              width = maxSize;
            } else {
              width = Math.round(width * (maxSize / height));
              height = maxSize;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          const base64 = canvas.toDataURL('image/jpeg', 0.85);
          resolve({ file, preview: URL.createObjectURL(file), base64 });
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const processed = await Promise.all(files.map(processImage));
    setImages(prev => [...prev, ...processed]);
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImages(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleSend = async () => {
    if ((!input.trim() && images.length === 0) || isLoading) return;

    const userText = input.trim();
    let contextPrefix = '';
    if (selectedBody) {
      contextPrefix = selectedFeature
        ? `[Context: The user has selected body '${selectedBody}', feature '${selectedFeature}' in the 3D viewer.]\n\n`
        : `[Context: The user has selected body '${selectedBody}' in the 3D viewer.]\n\n`;
    }

    let backendMessage;
    let displayContent;

    if (images.length > 0) {
      const contentArray = [];
      if (userText || contextPrefix) {
        contentArray.push({ type: 'text', text: contextPrefix + userText });
      }
      images.forEach(img => {
        contentArray.push({ type: 'image_url', image_url: { url: img.base64 } });
      });
      backendMessage = contentArray;

      // Display version: same structure but without context prefix
      const displayArray = [];
      if (userText) {
        displayArray.push({ type: 'text', text: userText });
      }
      images.forEach(img => {
        displayArray.push({ type: 'image_url', image_url: { url: img.base64 } });
      });
      displayContent = displayArray;
    } else {
      backendMessage = contextPrefix + userText;
      displayContent = userText;
    }

    const userMessage = { role: 'user', content: displayContent };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setImages([]);
    setIsLoading(true);
    setToolCalls([]);
    setPlanText('');

    try {
      await streamMessage(
        backendMessage,
        messages,
        // onPlan
        (content) => setPlanText(content),
        // onToolCall
        (name, args) => {
          setToolCalls(prev => [...prev, { name, args, status: 'running' }]);
        },
        // onToolResult
        (name, success, message, modelData) => {
          setToolCalls(prev => {
            // Find the *last* running call with this name (not the first)
            const lastIdx = prev.reduce((acc, tc, i) =>
              tc.name === name && tc.status === 'running' ? i : acc, -1);
            if (lastIdx === -1) return prev;
            return prev.map((tc, i) =>
              i === lastIdx
                ? { ...tc, status: success ? 'done' : 'error', message }
                : tc
            );
          });
          // Live 3D preview: update viewer after successful geometry tools
          if (modelData?.body_meshes?.length) {
            onModelUpdate(modelData.model_url, modelData.step_url, modelData.parts, modelData.feature_tree, modelData.body_meshes);
          }
        },
        // onDone
        (data) => {
          const assistantMessage = { role: 'assistant', content: data.response };
          setMessages(prev => [...prev, assistantMessage]);
          setToolCalls([]);
          setPlanText('');
          setIsLoading(false);
          onModelUpdate(data.model_url, data.step_url, data.parts, data.feature_tree, data.body_meshes);
        },
        // onError
        (error) => {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `Error: ${error}. Make sure the backend is running.`
          }]);
          setToolCalls([]);
          setPlanText('');
          setIsLoading(false);
        },
        // onReview
        (reviewEvent) => {
          setToolCalls(prev => [...prev, {
            name: reviewEvent.tool || 'review',
            args: '',
            status: reviewEvent.action === 'skip' ? 'skipped' : 'reviewing',
            message: reviewEvent.reason || reviewEvent.message
          }]);
        }
      );
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${error.message}. Make sure the backend is running.`
      }]);
      setToolCalls([]);
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleReset = async () => {
    if (isLoading) return;
    setMessages([]);
    setToolCalls([]);
    setImages([]);
    if (onReset) await onReset();
  };

  const renderUserContent = (content) => {
    if (typeof content === 'string') {
      return content;
    }
    if (Array.isArray(content)) {
      return (
        <div className="user-content-with-images">
          {content.map((item, i) => {
            if (item.type === 'text') {
              return <span key={i}>{item.text}</span>;
            }
            if (item.type === 'image_url') {
              return <img key={i} src={item.image_url.url} alt="Attached" className="chat-image-thumbnail" />;
            }
            return null;
          })}
        </div>
      );
    }
    return String(content);
  };

  const getToolStatusIcon = (status) => {
    switch (status) {
      case 'running': return '...';
      case 'done': return '>';
      case 'error': return '!';
      case 'reviewing': return '\u21BB';
      case 'skipped': return '\u2013';
      default: return '?';
    }
  };

  return (
    <div className="chat-panel">
      <div className="chat-panel-header">
        <span>Chat</span>
        <button className="reset-btn" onClick={handleReset} disabled={isLoading} title="New session">
          New Session
        </button>
      </div>
      <div className="messages-container">
        {messages.length === 0 && (
          <div className="welcome-message">
            <h2>Welcome to Agentic CAD!</h2>
            <p>Describe what you want to create, and I'll help you build it.</p>
            <div className="examples">
              <p><strong>Try:</strong></p>
              <ul>
                <li>"Create a box that is 50mm x 30mm x 20mm"</li>
                <li>"Make a cylinder with radius 10mm and height 40mm"</li>
                <li>"Create a phone stand"</li>
              </ul>
            </div>
          </div>
        )}
        {messages.map((msg, idx) => (
          <div key={idx} className={`message ${msg.role}`}>
            <div className="message-content">
              {msg.role === 'assistant' && (
                <span className="message-label">AI</span>
              )}
              {msg.role === 'assistant'
                ? <ReactMarkdown>{msg.content}</ReactMarkdown>
                : renderUserContent(msg.content)}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="message assistant">
            <div className="message-content">
              {planText && (
                <div className="plan-block">
                  <div className="plan-label">Build Plan</div>
                  <ReactMarkdown>{planText}</ReactMarkdown>
                </div>
              )}
              {!planText && toolCalls.length === 0 && (
                <div className="loading">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              )}
              {toolCalls.length > 0 && (
                <div className="tool-call-feed">
                  {toolCalls.map((tc, idx) => (
                    <div key={idx} className={`tool-call-item ${tc.status}`}>
                      <span className="tool-call-status">
                        {getToolStatusIcon(tc.status)}
                      </span>
                      <span className="tool-call-name">{tc.name}</span>
                      {tc.message && <span className="tool-call-msg">{tc.message}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="input-area">
        {images.length > 0 && (
          <div className="image-previews">
            {images.map((img, idx) => (
              <div key={idx} className="image-preview">
                <img src={img.preview} alt="Preview" />
                <button className="remove-image" onClick={() => removeImage(idx)}>&times;</button>
              </div>
            ))}
          </div>
        )}
        {selectedBody && (
          <div className="selection-indicator">
            Selected: {selectedBody}{selectedFeature ? ` > ${selectedFeature}` : ''}
          </div>
        )}
        <div className="input-container">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageSelect}
            accept="image/*"
            multiple
            hidden
          />
          <button
            className="image-upload-btn"
            onClick={() => fileInputRef.current?.click()}
            title="Attach image"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
          </button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={isLoading ? "Type your next message..." : "Describe what you want to create..."}
          />
          <button className="send-btn" onClick={handleSend} disabled={isLoading || (!input.trim() && images.length === 0)}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChatPanel;
